import { getBlogPostBySlug, getBlogPosts } from "../content/blog";

// In-memory cache for fetched blog post markdown contents
const contentCache = new Map();

/**
 * Strips markdown syntax and calculates estimated reading time in minutes
 * Assumes average reading speed of 200 words per minute
 * @param {string} markdownText 
 * @returns {number} reading time in minutes (minimum 1)
 */
export const calculateReadingTime = (markdownText) => {
  if (!markdownText) return 1;

  // Strip code blocks, HTML tags, markdown links and images
  const cleanText = markdownText
    .replace(/```[\s\S]*?```/g, "")
    .replace(/<[^>]+>/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[#*_~`>-]/g, " ")
    .trim();

  const words = cleanText.split(/\s+/).filter(Boolean).length;
  const minutes = Math.ceil(words / 200);
  return Math.max(1, minutes);
};

/**
 * Pre-fetches a blog post markdown file and preloads its cover image
 * Stores the markdown text in an in-memory cache for instant 0ms access
 * @param {string} slug
 */
export const prefetchBlogPost = async (slug) => {
  const post = getBlogPostBySlug(slug);
  if (!post) return;

  // 1. Preload cover image if present
  if (post.coverImage) {
    const img = new Image();
    img.src = post.coverImage;
  }

  // 2. Fetch markdown if not already cached
  if (contentCache.has(slug)) {
    return contentCache.get(slug);
  }

  try {
    const response = await fetch(post.contentPath);
    if (response.ok) {
      const text = await response.text();
      contentCache.set(slug, text);
      return text;
    }
  } catch (err) {
    // Silently handle prefetch errors; normal fetch in page will handle it if user navigates
    console.debug(`Prefetch error for ${slug}:`, err);
  }
};

/**
 * Get cached blog post content synchronously if available
 * @param {string} slug 
 * @returns {string|null}
 */
export const getCachedBlogContent = (slug) => {
  return contentCache.get(slug) || null;
};

/**
 * Set cached blog post content
 * @param {string} slug 
 * @param {string} text 
 */
export const setCachedBlogContent = (slug, text) => {
  contentCache.set(slug, text);
};

/**
 * Get previous and next blog posts relative to the current post
 * @param {string} currentSlug 
 * @returns {{ prevPost: object|null, nextPost: object|null }}
 */
export const getAdjacentBlogPosts = (currentSlug) => {
  const allPosts = getBlogPosts("All");
  const currentIndex = allPosts.findIndex((p) => p.slug === currentSlug);

  if (currentIndex === -1) {
    return { prevPost: null, nextPost: null };
  }

  const prevPost = currentIndex > 0 ? allPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null;

  return { prevPost, nextPost };
};

